import { create } from 'zustand';
import useAuthStore from './authStore';

const useUserStore = create((set, get) => ({
    // User profile data
    profile: null,
    isLoading: false,
    error: null,

    // Get user profile
    fetchProfile: async () => {
        const { token } = useAuthStore.getState();
        if (!token) {
            await new Promise((r) => setTimeout(r, 50));
        }

        set({ isLoading: true, error: null });
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/users/profile');
            
            // ✅ Keep nested structure as returned by API
            const profileData = response.data;
            
            set({
                profile: profileData,  // ← Don't flatten!
                isLoading: false,
                error: null,
            });
        } catch (error) {
            set({
                isLoading: false,
                error: error.response?.data?.message || 'Failed to fetch profile',
            });
        }
    },

    // Update user profile
    updateProfile: async (profileData) => {
        const { user } = useAuthStore.getState();
        if (!user) return { success: false, error: 'User not found' };

        set({ isLoading: true, error: null });
        try {
            const { default: api } = await import('../services/api');
            const response = await api.put('/users/profile', profileData);  // ✅ PUT request
            
            // ✅ Use the response data (nested structure)
            const updatedProfile = response.data;
            
            set({
                profile: updatedProfile,  // ← Keep nested structure
                isLoading: false,
                error: null,
            });

            // Update auth store user data
            useAuthStore.getState().updateProfile(updatedProfile?.user || {});

            return { success: true, profile: updatedProfile };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Profile update failed';
            set({
                isLoading: false,
                error: errorMessage,
            });
            return { success: false, error: errorMessage };
        }
    },

    // Clear error
    clearError: () => set({ error: null }),

    // Get user type
    getUserType: () => {
        const { user } = useAuthStore.getState();
        return user?.user_type || 'learner';
    },

    // Check if user is learner
    isLearner: () => {
        const { user } = useAuthStore.getState();
        return user?.user_type === 'learner';
    },

    // Check if user is trainer
    isTrainer: () => {
        const { user } = useAuthStore.getState();
        return user?.user_type === 'trainer';
    },

    // Check if user is policymaker
    isPolicymaker: () => {
        const { user } = useAuthStore.getState();
        return user?.user_type === 'policymaker';
    },
}));

export default useUserStore;